import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Sparkles, Trophy, ArrowRight, DollarSign, Copy, Share2, Twitter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPublic() {
  const nav = useNavigate();
  const [refs, setRefs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/referral/leaderboard/monthly`).then((r) => setRefs(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen w-full grid-bg" data-testid="referral-public">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
        {/* HERO */}
        <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#F0ABFC] mb-3">
          <Users size={14} /> REFER · EARN · REPEAT
        </div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tighter text-white leading-none">
          Share a link.<br />
          <span className="text-[#F0ABFC]" style={{ textShadow: "0 0 30px rgba(240,171,252,0.6)" }}>Earn real cash.</span>
        </h1>
        <p className="text-sm md:text-base text-white/60 mt-6 max-w-xl tracking-wider">
          Every friend that unlocks a Season Pass through your link gets{" "}
          <span className="text-white">15% off</span> — and you get{" "}
          <span className="text-[#39FF14]">$1.00</span> credit paid straight to your Stripe account.
        </p>
        <div className="flex flex-wrap gap-3 mt-8">
          <button
            data-testid="referral-cta-open"
            onClick={() => nav("/referrals/dashboard")}
            className="px-6 py-3 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors text-xs tracking-[0.3em] flex items-center gap-2"
            style={{ boxShadow: "0 0 22px rgba(240,171,252,0.4)" }}
          >
            OPEN MY DASHBOARD <ArrowRight size={14} />
          </button>
          <button
            data-testid="referral-cta-menu"
            onClick={() => nav("/")}
            className="px-6 py-3 border border-white/20 text-white/60 hover:border-cyan-300 hover:text-cyan-300 transition-colors text-xs tracking-[0.3em]"
          >
            ← BACK TO GAME
          </button>
        </div>

        {/* HOW IT WORKS */}
        <div className="grid md:grid-cols-3 gap-4 mt-16">
          <Step icon={<Sparkles size={18} />} n="01" title="CREATE" body="Enter your email, get a personal /r/CODE link + Stripe coupon in seconds." />
          <Step icon={<Share2 size={18} />} n="02" title="SHARE" body="Post it anywhere — Discord, Twitter, WhatsApp, a Twitch panel." />
          <Step icon={<DollarSign size={18} />} n="03" title="EARN" body="$1 credit for every confirmed sale. Cash out to Stripe whenever you want." />
        </div>

        {/* SOCIAL SHARE */}
        <div className="mt-12 border border-white/10 bg-black/40 backdrop-blur-md p-6">
          <div className="text-[10px] tracking-[0.4em] text-white/50 mb-3 flex items-center gap-2"><Twitter size={12} /> ONE-CLICK POST TEMPLATES</div>
          <div className="grid md:grid-cols-2 gap-3">
            <a
              data-testid="share-twitter"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Escape a parking lot full of murderous cars. This game will not stop. Try it 👇")}&url=${encodeURIComponent(window.location.origin + "/")}`}
              target="_blank" rel="noreferrer"
              className="px-4 py-2 border border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors text-[10px] tracking-[0.3em] flex items-center gap-2 justify-center"
            >
              <Twitter size={12} /> POST ON TWITTER
            </a>
            <button
              data-testid="share-copy"
              onClick={() => navigator.clipboard?.writeText("Escape a parking lot full of murderous cars — 🕹️ " + window.location.origin + "/")}
              className="px-4 py-2 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black transition-colors text-[10px] tracking-[0.3em] flex items-center gap-2 justify-center"
            >
              <Copy size={12} /> COPY DEFAULT PITCH
            </button>
          </div>
        </div>

        {/* LEADERBOARD PREVIEW */}
        <div className="mt-12 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="referral-public-leaderboard">
          <div className="text-[10px] tracking-[0.4em] text-[#F0ABFC] mb-3 flex items-center gap-2">
            <Trophy size={12} /> TOP REFERRERS · THIS MONTH
          </div>
          {refs.length === 0 ? (
            <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-8">BE THE FIRST · YOUR NAME COULD BE HERE</div>
          ) : (
            <div className="space-y-2">
              {refs.slice(0, 5).map((r) => (
                <div key={r.code} className="grid grid-cols-12 items-center text-xs border-b border-white/5 py-2">
                  <div className="col-span-1 text-white/40 font-mono">#{r.rank}</div>
                  <div className="col-span-6 flex items-center gap-2">
                    {r.rank === 1 && <Trophy size={12} className="text-[#FFD400]" />}
                    <span className="text-white font-mono">{r.handle}</span>
                  </div>
                  <div className="col-span-2 text-right tabular-nums text-[#39FF14]">{r.conversions}</div>
                  <div className="col-span-3 text-right tabular-nums text-[#FFD400]">${(r.earned_cents / 100).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ icon, n, title, body }) {
  return (
    <div className="border border-white/10 bg-black/40 backdrop-blur-md p-6" style={{ boxShadow: "inset 0 0 22px rgba(240,171,252,0.06)" }}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#F0ABFC]">
        {icon} <span>{n}</span>
      </div>
      <div className="mt-3 text-xs tracking-[0.3em] text-white">{title}</div>
      <div className="mt-2 text-xs text-white/60 leading-relaxed">{body}</div>
    </div>
  );
}
